import { Badge, cardClass, Table as UiTable } from '@ledgerloom/ui';
import { useSearchParams } from 'react-router-dom';
import { useApi, usePageTitle } from '../hooks';
import { Empty, ErrorBanner, Expandable, ListSkeleton, Time } from '../components/ui';
import { Tabs } from '../components/Tabs';
import { EmbedCode } from '../components/EmbedCode';
import { bp, count, day, money } from '../format';

// Marketing: campaigns with what they sent and what came of it, segments,
// emails, forms, landing pages, and sequences. Agents draft these; people
// approve broadcasts. Patterns: navigation/tabs and data-display/table.

type Tab = 'campaigns' | 'segments' | 'emails' | 'forms' | 'pages' | 'sequences';
const TABS: Array<[Tab, string]> = [['campaigns', 'Campaigns'], ['segments', 'Segments'], ['emails', 'Emails'], ['forms', 'Forms'], ['pages', 'Landing pages'], ['sequences', 'Sequences']];

export function Marketing() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'campaigns';
  usePageTitle('Marketing');
  return (
    <div className="stack">
      <div className="page-head"><div><h1>Marketing</h1><p>Every send passes the consent check and the gate. Agents draft; a person approves each broadcast.</p></div></div>
      <Tabs label="Marketing" value={tab} tabs={TABS} onChange={(t) => setParams({ tab: t }, { replace: true })}>
        {tab === 'campaigns' && <Campaigns />}
        {tab === 'segments' && <Segments />}
        {tab === 'emails' && <Emails />}
        {tab === 'forms' && <Forms />}
        {tab === 'pages' && <Pages />}
        {tab === 'sequences' && <Sequences />}
      </Tabs>
    </div>
  );
}

function Table<T>({ path, label, empty, head, row }: { path: string; label: string; empty: string; head: string[]; row: (x: T) => React.ReactNode }) {
  const data = useApi<T[]>(path);
  return (
    <section className={cardClass}>
      <ErrorBanner error={data.error} />
      {data.loading && !data.data ? <ListSkeleton rows={3} label={`Loading ${label}`} /> : !data.data?.length ? <Empty title={empty} /> : (
        <UiTable label={label}>
          <caption className="sr-only">{label}</caption>
          <thead><tr>{head.map((h) => <th key={h} scope="col">{h}</th>)}</tr></thead>
          <tbody>{data.data.map(row)}</tbody>
        </UiTable>
      )}
    </section>
  );
}

// A program's integrated plan (campaign_program.create v2): each part by stage, channel, role, cadence, and who does it.
interface PlanPart { channel: string; stage: string; role: string; cadence?: string; by: 'ledgerloom' | 'person' }
const words = (s: string) => s.replace(/_/g, ' ');
const Plan = ({ parts }: { parts: PlanPart[] }) => (
  <Expandable label={[`Show the plan (${parts.length} ${parts.length === 1 ? 'part' : 'parts'})`, 'Hide the plan']}>
    <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
      {parts.map((p, i) => <li key={i}><strong>{words(p.stage)}, {words(p.channel)}</strong>: {p.role}{p.cadence ? ` (${p.cadence})` : ''}{p.by === 'person' ? ' · done by a person' : ''}</li>)}
    </ul>
  </Expandable>
);
interface Campaign { id: string; name: string; goal: string; status: string; starts_on: string | null; stopped_reason: string | null; plan: { parts?: PlanPart[] }; stats: { sent: number; delivered: number; opened: number; clicked: number; bounced: number; unsubscribed?: number; blocked: number }; conversions: number; attributed_value_minor: number; currency: string | null }
const Campaigns = () => (
  <Table<Campaign> path="/v1/campaigns" label="Campaigns" empty="No campaigns yet" head={['Campaign', 'Status', 'Sent', 'Opened', 'Clicked', 'Bounced', 'Conversions', 'Attributed value']}
    row={(c) => <tr key={c.id}><td>{c.name}<div className="faint">{c.goal}</div>{c.stopped_reason && <Badge tone="danger" className="mt-1">Stopped: {c.stopped_reason}</Badge>}{!!c.plan?.parts?.length && <Plan parts={c.plan.parts} />}</td><td><Badge>{c.status}</Badge></td><td>{count(c.stats.sent)}</td><td>{count(c.stats.opened)} <span className="faint">{c.stats.sent ? bp(Math.round((c.stats.opened * 10000) / c.stats.sent)) : ''}</span></td><td>{count(c.stats.clicked)}</td><td>{count(c.stats.bounced)}</td><td>{count(c.conversions)}</td><td>{money(c.attributed_value_minor, c.currency ?? 'USD', { cents: false })}</td></tr>} />
);
interface Segment { id: string; name: string; description: string | null; type: string; status: string; member_count: number; refreshed_at: string | null }
const Segments = () => (
  <Table<Segment> path="/v1/segments" label="Segments" empty="No segments yet" head={['Segment', 'Type', 'Members', 'Refreshed', 'Status']}
    row={(s) => <tr key={s.id}><td>{s.name}{s.description && <div className="faint">{s.description}</div>}</td><td>{s.type === 'active' ? 'Filter' : 'Static list'}</td><td>{count(s.member_count)}</td><td className="small">{s.refreshed_at ? <Time iso={s.refreshed_at} /> : 'Not yet'}</td><td><Badge>{s.status}</Badge></td></tr>} />
);
interface Email { id: string; name: string; subject: string; category: string; subscription_type: string | null; status: string; sends: number; updated_at: string }
const Emails = () => (
  <Table<Email> path="/v1/emails" label="Emails" empty="No emails yet" head={['Email', 'Subject', 'Kind', 'Status', 'Sends', 'Updated']}
    row={(e) => <tr key={e.id}><td>{e.name}</td><td className="small">{e.subject}</td><td className="small">{e.category}{e.subscription_type ? ` · ${e.subscription_type}` : ''}</td><td><Badge>{e.status}</Badge></td><td>{e.sends}</td><td className="small"><Time iso={e.updated_at} /></td></tr>} />
);
interface Form { id: string; name: string; status: string; submissions: number; endpoint: string; embed: string; fields: unknown[] }
const Forms = () => (
  <Table<Form> path="/v1/forms" label="Forms" empty="No forms yet" head={['Form', 'Fields', 'Submissions', 'Endpoint', 'Status']}
    row={(f) => <tr key={f.id}><td>{f.name}{f.status === 'active' && <EmbedCode name={f.name} code={f.embed} />}</td><td>{f.fields?.length ?? 0}</td><td>{f.submissions}</td><td className="mono small">{f.endpoint}</td><td><Badge>{f.status}</Badge></td></tr>} />
);
interface Page { id: string; slug: string; title: string; status: string; visits: number; submissions: number; published_at: string | null }
const Pages = () => (
  <Table<Page> path="/v1/landing-pages" label="Landing pages" empty="No landing pages yet" head={['Page', 'Status', 'Visits', 'Form submissions', 'Published']}
    row={(p) => <tr key={p.id}><td>{p.title}<div className="faint mono">/pages/{p.slug}</div></td><td><Badge>{p.status}</Badge></td><td>{p.visits}</td><td>{p.submissions}</td><td className="small">{day(p.published_at)}</td></tr>} />
);
interface Sequence { id: string; name: string; purpose: string; status: string; steps: unknown[]; enrollments: Record<string, number> | null }
const Sequences = () => (
  <Table<Sequence> path="/v1/sequences" label="Sequences" empty="No sequences yet" head={['Sequence', 'Purpose', 'Steps', 'Enrolled', 'Status']}
    row={(q) => <tr key={q.id}><td>{q.name}</td><td>{q.purpose}</td><td>{q.steps.length}</td><td className="small">{Object.entries(q.enrollments ?? {}).map(([k, v]) => `${v} ${k}`).join(', ') || '0'}</td><td><Badge>{q.status}</Badge></td></tr>} />
);
